import React, { useState, useEffect } from 'react';
import './AdminDashboard.css';
import { getBackendUrl } from '../../utils/backendUrl';

// Type definitions for bettor performance metrics
interface BettorPerformance {
  twitter_username: string;
  totalWagered: number;
  games_played: number;
  totalPaidOut: number;
  bestStreak: number;
  biggestWin: number;
  best_profit: number; // Legacy field for compatibility
}

interface StreakPerformer {
  twitter_username: string;
  bestStreak: number;
  games_played: number;
  totalWagered: number;
}

interface BiggestWinner {
  twitter_username: string;
  biggestWin: number;
  games_played: number;
  totalWagered: number;
}

interface ReferrerStats {
  wallet_address: string;
  referral_code: string;
  code_created_at: string;
  total_referrals: number;
  active_referrals: number;
  total_volume: number;
  potential_earnings: number;
  actual_claimed: number;
  claimable_now: number;
  last_claim_at: string | null;
  last_claim_amount: number | null;
}

interface ReferralMetrics {
  topReferrers: ReferrerStats[];
  totalReferrers: number;
  totalReferrals: number;
  totalVolume: number;
  totalPotentialEarnings: number;
  totalClaimed: number;
  totalClaimable: number;
}

interface MetricsData {
  totalGames: number; // Total game sessions
  totalActiveUsers: number; // Total players
  averageProfitPercentage: number; // Removed but kept for compatibility
  totalProfitableGames: number; // Sessions that cashed out
  repeatPlayersPercentage: number; // Repeat players percentage
  retentionRates: {
    oneDay: number;
  };
  topPerformers: Array<BettorPerformance>;
  dailyStats: Array<{
    date: string;
    games: number; // Sessions per day
    users: number; // Players per day
    avgProfit: number;
  }>;
  // SOL metrics
  totalSolVolume?: number;
  totalSolEarned?: number;
  
  // NEW METRICS
  topBettorsByVolume?: Array<BettorPerformance>;
  bestStreakPerformers?: Array<StreakPerformer>;
  biggestWinners?: Array<BiggestWinner>;
}

const AdminDashboard: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [metrics, setMetrics] = useState<MetricsData | null>(null);
  const [referralMetrics, setReferralMetrics] = useState<ReferralMetrics | null>(null);
  const [loadingReferrals, setLoadingReferrals] = useState(false);
  const [timeRange, setTimeRange] = useState<'daily' | 'weekly' | 'monthly' | 'all'>('monthly');
  const [chartRange, setChartRange] = useState<'7d' | '30d'>('7d');
  const canvasRef = React.useRef<HTMLCanvasElement>(null);

  // Get backend URL using centralized utility

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) {
      setError('Please enter a password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${getBackendUrl()}/api/admin/metrics?timeRange=daily`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Origin': window.location.origin,
          'X-Admin-Password': password
        }
      });

      if (response.ok) {
        setIsAuthenticated(true);
        loadMetrics(); // This will now load both metrics and chart data separately
      } else {
        setError('Invalid admin password');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Connection error. Make sure backend is running.');
    } finally {
      setLoading(false);
    }
  };

  const loadMetrics = async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    try {

      
      // Get metrics for the selected time range
      const metricsResponse = await fetch(`${getBackendUrl()}/api/admin/metrics?timeRange=${timeRange}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Origin': window.location.origin,
          'X-Admin-Password': password
        }
      });

      // Always get chart data for 'all' time range to show complete trends
      const chartResponse = await fetch(`${getBackendUrl()}/api/admin/metrics?timeRange=all`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Origin': window.location.origin,
          'X-Admin-Password': password
        }
      });

      if (metricsResponse.ok && chartResponse.ok) {
        const metricsResult = await metricsResponse.json();
        const chartResult = await chartResponse.json();
        
        // Combine metrics from selected timeRange with chart data from all time
        const combinedData = {
          ...metricsResult.data,
          dailyStats: chartResult.data.dailyStats // Always use complete daily stats for chart
        };
        
        setMetrics(combinedData);
      } else if (metricsResponse.status === 401 || chartResponse.status === 401) {
        setIsAuthenticated(false);
        setError('Session expired. Please login again.');
      } else {
        setError('Failed to load metrics');
      }
    } catch (error) {
      console.error('Failed to load metrics:', error);
      setError('Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };

  const loadReferralMetrics = async () => {
    if (!isAuthenticated) return;

    setLoadingReferrals(true);
    try {
      const response = await fetch(`${getBackendUrl()}/api/admin/referral-metrics?limit=20`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Origin': window.location.origin,
          'X-Admin-Password': password
        }
      });

      if (response.ok) {
        const result = await response.json();
        setReferralMetrics(result.data);
      } else if (response.status === 401) {
        setIsAuthenticated(false);
        setError('Session expired. Please login again.');
      } else {
        console.error('Failed to load referral metrics');
      }
    } catch (error) {
      console.error('Failed to load referral metrics:', error);
    } finally {
      setLoadingReferrals(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    setMetrics(null);
    setError('');
  };

  const handleTimeRangeChange = (newRange: 'daily' | 'weekly' | 'monthly' | 'all') => {
    setTimeRange(newRange);
  };

  const handleChartRangeChange = (newRange: '7d' | '30d') => {
    setChartRange(newRange);
  };

  // Draw trends chart on canvas
  const drawTrendsChart = (dailyStats: MetricsData['dailyStats']) => {
    const canvas = canvasRef.current;
    if (!canvas || !dailyStats || dailyStats.length === 0) {
      return;
    }
    
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    
    const width = canvas.width;
    const height = canvas.height;
    
    // Clear canvas
    ctx.clearRect(0, 0, width, height);
    
    // Filter stats based on chart range
    const days = chartRange === '7d' ? 7 : 30;
    const filteredStats = dailyStats.slice(-days);
    
    if (filteredStats.length === 0) {
      return;
    }
    
    // Chart settings - adjust padding based on screen size
    const isMobile = window.innerWidth <= 768;
    const padding = isMobile ? 50 : 60;
    const bottomPadding = isMobile ? 80 : 70; // Extra space for X-axis labels and legend
    const chartWidth = width - (padding * 2);
    const chartHeight = height - padding - bottomPadding;
    
    // Get data ranges
    const maxBets = Math.max(...filteredStats.map(d => d.games)); // games field contains bets
    const maxUsers = Math.max(...filteredStats.map(d => d.users));
    const maxValue = Math.max(maxBets, maxUsers);
    
    if (maxValue === 0) {
      return;
    }
    
    // Draw background grid
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 1;
    
    // Horizontal grid lines
    for (let i = 0; i <= 5; i++) {
      const y = padding + (chartHeight / 5) * i;
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(width - padding, y);
      ctx.stroke();
      
      // Y-axis labels
      const value = Math.round(maxValue - (maxValue / 5) * i);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.font = '12px monospace';
      ctx.textAlign = 'right';
      ctx.fillText(value.toString(), padding - 10, y + 4);
    }
    
    // Draw data lines and points
    if (filteredStats.length >= 1) {
      const stepX = filteredStats.length > 1 ? chartWidth / (filteredStats.length - 1) : chartWidth / 2;
      
      // Bets line (purple) - only draw line if multiple points
      if (filteredStats.length > 1) {
        ctx.strokeStyle = '#A019FF';
        ctx.lineWidth = 3;
        ctx.beginPath();
        
        filteredStats.forEach((stat, index) => {
          const x = padding + stepX * index;
          const y = padding + chartHeight - (stat.games / maxValue) * chartHeight; // games field contains bets
          
          if (index === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        });
        ctx.stroke();
      }
      
      // Users line (green) - only draw line if multiple points
      if (filteredStats.length > 1) {
        ctx.strokeStyle = '#00ff88';
        ctx.lineWidth = 3;
        ctx.beginPath();
        
        filteredStats.forEach((stat, index) => {
          const x = padding + stepX * index;
          const y = padding + chartHeight - (stat.users / maxValue) * chartHeight;
          
          if (index === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        });
        ctx.stroke();
      }
      
      // Draw data points
      filteredStats.forEach((stat, index) => {
        const x = padding + stepX * index;
        
        // Bets point
        const betsY = padding + chartHeight - (stat.games / maxValue) * chartHeight; // games field contains bets
        ctx.fillStyle = '#A019FF';
        ctx.beginPath();
        ctx.arc(x, betsY, 4, 0, 2 * Math.PI);
        ctx.fill();
        
        // Users point
        const usersY = padding + chartHeight - (stat.users / maxValue) * chartHeight;
        ctx.fillStyle = '#00ff88';
        ctx.beginPath();
        ctx.arc(x, usersY, 4, 0, 2 * Math.PI);
        ctx.fill();
      });
    }
    
    // Draw legend - adjust position based on mobile
    const legendY = height - (isMobile ? 50 : 40);
    const legendTextY = height - (isMobile ? 45 : 35);
    
    ctx.fillStyle = '#A019FF';
    ctx.fillRect(padding, legendY, 15, 3);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.font = isMobile ? '12px -apple-system, BlinkMacSystemFont, sans-serif' : '14px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Sessions per Day', padding + 25, legendTextY);
    
    ctx.fillStyle = '#00ff88';
    const secondLegendX = isMobile ? padding + 140 : padding + 170;
    ctx.fillRect(secondLegendX, legendY, 15, 3);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.fillText('Players per Day', secondLegendX + 25, legendTextY);
    
    // X-axis labels (show every few days) - adjust position for mobile
    const labelStep = Math.max(1, Math.floor(filteredStats.length / (isMobile ? 4 : 6)));
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.font = isMobile ? '9px monospace' : '10px monospace';
    ctx.textAlign = 'center';
    
    filteredStats.forEach((stat, index) => {
      if (index % labelStep === 0 || index === filteredStats.length - 1) {
        const x = filteredStats.length > 1 
          ? padding + (chartWidth / (filteredStats.length - 1)) * index
          : padding + chartWidth / 2; // Center single point
        const date = new Date(stat.date);
        const label = `${date.getMonth() + 1}/${date.getDate()}`;
        const labelY = height - (isMobile ? 25 : 15);
        ctx.fillText(label, x, labelY);
      }
    });
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadMetrics();
      loadReferralMetrics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, isAuthenticated]);

  useEffect(() => {
    if (metrics && metrics.dailyStats) {
      // Add a small delay to ensure canvas is ready
      setTimeout(() => {
        drawTrendsChart(metrics.dailyStats);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metrics, chartRange]);
  
  // Force redraw when canvas ref changes
  useEffect(() => {
    if (canvasRef.current && metrics && metrics.dailyStats) {
      setTimeout(() => {
        drawTrendsChart(metrics.dailyStats);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasRef.current]);

  // Redraw chart on window resize for mobile responsiveness
  useEffect(() => {
    const handleResize = () => {
      if (metrics && metrics.dailyStats) {
        setTimeout(() => drawTrendsChart(metrics.dailyStats), 100);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metrics]);

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const getPeriodInfo = () => {
    const now = new Date();
    let periodDesc = '';
    let periodIcon = '';
    
    switch(timeRange) {
      case 'daily':
        periodDesc = `Today (${now.toLocaleDateString()})`;
        periodIcon = '📅';
        break;
      case 'weekly':
        const startOfWeek = new Date(now);
        const dayOfWeek = now.getDay();
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        startOfWeek.setDate(now.getDate() - daysFromMonday);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        periodDesc = `This week (${startOfWeek.toLocaleDateString()} - ${endOfWeek.toLocaleDateString()})`;
        periodIcon = '📊';
        break;
      case 'monthly':
        periodDesc = `${now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;
        periodIcon = '📈';
        break;
      case 'all':
        periodDesc = 'All time data';
        periodIcon = '🌍';
        break;
    }
    
    return { periodDesc, periodIcon };
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-login">
        <div className="admin-login-form">
          <h1>🎮 FEEN Admin</h1>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              disabled={loading}
            />
            <button type="submit" disabled={loading}>
              {loading ? 'Authenticating...' : 'Access Dashboard'}
            </button>
          </form>
          {error && <div className="admin-error">{error}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-content">
        <div className="admin-header">
          <h1>📊 FEEN Metrics</h1>
          <button onClick={handleLogout} className="admin-logout">Logout</button>
        </div>

      <div className="admin-controls">
        <div className="time-controls">
          {(['daily', 'weekly', 'monthly', 'all'] as const).map((range) => (
            <button
              key={range}
              className={`time-btn ${timeRange === range ? 'active' : ''}`}
              onClick={() => handleTimeRangeChange(range)}
            >
              {range.charAt(0).toUpperCase() + range.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="filter-explanation">
        <p>📊 <strong>Daily</strong>: Today's activity | <strong>Weekly</strong>: This week's activity | <strong>Monthly</strong>: This month's activity</p>
        <p>🔄 <strong>Repeat Players</strong>: Users who played multiple sessions in current period | <strong>Retention</strong>: Users who returned from previous period</p>
        {metrics && (
          <div className="period-info">
            <span style={{ color: 'rgba(255,255,255,0.8)' }}>
              {getPeriodInfo().periodIcon} <strong>{getPeriodInfo().periodDesc}</strong>
            </span>
            <span style={{ margin: '0 15px', color: 'rgba(255,255,255,0.6)' }}>•</span>
            <span style={{ color: 'rgba(255,255,255,0.7)' }}>
              {metrics.totalGames} game sessions by {metrics.totalActiveUsers} players
            </span>
          </div>
        )}
      </div>

      {loading && (
        <div className="admin-loading">
          <div className="loading-spinner"></div>
          <p>Loading metrics...</p>
        </div>
      )}

      {metrics && !loading && (
        <div className="admin-metrics">
          <div className="metrics-grid">
            <div className="metric-card primary">
              <div className="admin-metric-icon">🎮</div>
              <div className="admin-metric-title">Total Sessions</div>
              <div className="admin-metric-value">{formatNumber(metrics.totalGames)}</div>
            </div>

            <div className="metric-card primary">
              <div className="admin-metric-icon">👥</div>
              <div className="admin-metric-title">Active Players</div>
              <div className="admin-metric-value">{formatNumber(metrics.totalActiveUsers)}</div>
            </div>

            <div className="metric-card">
              <div className="admin-metric-icon">💰</div>
              <div className="admin-metric-title">Successful Cash Outs</div>
              <div className="admin-metric-value positive">
                {formatNumber(metrics.totalProfitableGames)}
              </div>
            </div>

            <div className="metric-card">
              <div className="admin-metric-icon">🔄</div>
              <div className="admin-metric-title">Repeat Players</div>
              <div className="admin-metric-value positive">
                {metrics.repeatPlayersPercentage.toFixed(1)}%
              </div>
            </div>

            <div className="metric-card">
              <div className="admin-metric-icon">📈</div>
              <div className="admin-metric-title">Retention Rate</div>
              <div className="admin-metric-value positive">
                {metrics.retentionRates.oneDay.toFixed(1)}%
              </div>
              <div className="admin-metric-subtitle">Returned from previous period</div>
            </div>

            {/* New SOL Trading Metrics */}
            {metrics.totalSolVolume !== undefined && (
              <div className="metric-card">
                <div className="admin-metric-icon">📈</div>
                <div className="admin-metric-title">Total SOL Volume</div>
                <div className="admin-metric-value">
                  {metrics.totalSolVolume.toFixed(4)} SOL
                </div>
                <div className="admin-metric-subtitle">Total amount wagered</div>
              </div>
            )}

            {metrics.totalSolEarned !== undefined && (
              <div className="metric-card">
                <div className="admin-metric-icon">💎</div>
                <div className="admin-metric-title">House Profit/Loss</div>
                <div className={`admin-metric-value ${metrics.totalSolEarned >= 0 ? 'positive' : 'negative'}`}>
                  {metrics.totalSolEarned >= 0 ? '+' : ''}{metrics.totalSolEarned.toFixed(4)} SOL
                </div>
                <div className="admin-metric-subtitle">Wagered minus paid out</div>
              </div>
            )}

            <div className="metric-card">
              <div className="admin-metric-icon">⭐</div>
              <div className="admin-metric-title">Top Player</div>
              <div className="admin-metric-value" style={{ fontSize: '18px' }}>
                {metrics.topPerformers[0]?.twitter_username || 'N/A'}
              </div>
              <div className="admin-metric-subtitle">
                {metrics.topPerformers[0] ? `${metrics.topPerformers[0].totalWagered.toFixed(2)} SOL • ${metrics.topPerformers[0].games_played} sessions` : 'No data'}
              </div>
            </div>
          </div>

          {/* Player Leaderboards */}
          {metrics.topBettorsByVolume && metrics.topBettorsByVolume.length > 0 && (
            <div className="leaderboard-section">
              <h2 className="section-title">💰 Top Players by Volume</h2>
              <div className="leaderboard-grid">
                {metrics.topBettorsByVolume.slice(0, 5).map((player: BettorPerformance, index: number) => (
                  <div key={index} className="leaderboard-item">
                    <div className="rank">#{index + 1}</div>
                    <div className="bettor-info">
                      <div className="bettor-name">{player.twitter_username}</div>
                      <div className="bettor-stats">
                        <span className="main-stat">{player.totalWagered.toFixed(4)} SOL</span>
                        <span className="sub-stat">{player.games_played} sessions</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {metrics.bestStreakPerformers && metrics.bestStreakPerformers.length > 0 && (
            <div className="leaderboard-section">
              <h2 className="section-title">🔥 Most Rounds Completed</h2>
              <div className="leaderboard-grid">
                {metrics.bestStreakPerformers.slice(0, 5).map((performer: StreakPerformer, index: number) => (
                  <div key={index} className="leaderboard-item">
                    <div className="rank">#{index + 1}</div>
                    <div className="bettor-info">
                      <div className="bettor-name">{performer.twitter_username}</div>
                      <div className="bettor-stats">
                        <span className="main-stat">{performer.bestStreak} rounds</span>
                        <span className="sub-stat">{performer.games_played} total sessions</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {metrics.biggestWinners && metrics.biggestWinners.length > 0 && (
            <div className="leaderboard-section">
              <h2 className="section-title">🏆 Biggest Payouts</h2>
              <div className="leaderboard-grid">
                {metrics.biggestWinners.slice(0, 5).map((winner: BiggestWinner, index: number) => (
                  <div key={index} className="leaderboard-item">
                    <div className="rank">#{index + 1}</div>
                    <div className="bettor-info">
                      <div className="bettor-name">{winner.twitter_username}</div>
                      <div className="bettor-stats">
                        <span className="main-stat">{winner.biggestWin.toFixed(4)} SOL</span>
                        <span className="sub-stat">{winner.games_played} total sessions</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Referral Metrics Section */}
          {referralMetrics && (
            <div className="referral-metrics-section">
              <h2 className="section-title">🎯 Referral Program Metrics</h2>
              
              {/* Referral Overview Cards */}
              <div className="metrics-grid" style={{ marginBottom: '2rem' }}>
                <div className="metric-card">
                  <div className="admin-metric-icon">👥</div>
                  <div className="admin-metric-title">Active Referrers</div>
                  <div className="admin-metric-value">{referralMetrics.totalReferrers}</div>
                  <div className="admin-metric-subtitle">
                    {referralMetrics.totalReferrals} total referrals
                  </div>
                </div>

                <div className="metric-card">
                  <div className="admin-metric-icon">💰</div>
                  <div className="admin-metric-title">Total Volume</div>
                  <div className="admin-metric-value">{referralMetrics.totalVolume.toFixed(2)} SOL</div>
                  <div className="admin-metric-subtitle">
                    From referral activity
                  </div>
                </div>

                <div className="metric-card">
                  <div className="admin-metric-icon">💸</div>
                  <div className="admin-metric-title">Total Earnings</div>
                  <div className="admin-metric-value">{referralMetrics.totalPotentialEarnings.toFixed(4)} SOL</div>
                  <div className="admin-metric-subtitle">
                    {referralMetrics.totalClaimed.toFixed(4)} claimed • {referralMetrics.totalClaimable.toFixed(4)} claimable
                  </div>
                </div>
              </div>

              {/* Top Referrers Table */}
              {loadingReferrals ? (
                <div className="admin-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading referral data...</p>
                </div>
              ) : (
                <div className="referral-table-container">
                  <h3 style={{ color: 'white', marginBottom: '1rem' }}>Top 10 Referrers (All Environments)</h3>
                  <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', marginBottom: '1rem' }}>
                    📊 Sorted by referrals, then volume • 🌍 Shows data from both development & production
                  </p>
                  <div className="referral-table-scroll">
                    <table className="referral-table">
                      <thead>
                        <tr>
                          <th>Rank</th>
                          <th>Referral Code</th>
                          <th>Wallet</th>
                          <th>Referrals</th>
                          <th>Total Volume</th>
                          <th>Potential Earnings</th>
                          <th>Claimed</th>
                          <th>Claimable</th>
                        </tr>
                      </thead>
                      <tbody>
                        {referralMetrics.topReferrers.slice(0, 10).map((referrer, index) => {
                          const hasPlayed = referrer.total_volume > 0;
                          const hasEarnings = referrer.potential_earnings > 0;
                          
                          return (
                            <tr key={referrer.wallet_address} className={!hasPlayed ? 'no-volume-row' : ''}>
                              <td className="rank-cell">#{index + 1}</td>
                              <td className="code-cell">
                                <code>{referrer.referral_code}</code>
                              </td>
                              <td className="wallet-cell">
                                <span title={referrer.wallet_address}>
                                  {referrer.wallet_address.slice(0, 4)}...{referrer.wallet_address.slice(-4)}
                                </span>
                              </td>
                              <td className="stat-cell highlight-referrals">
                                {referrer.total_referrals}
                                {referrer.total_referrals > 0 && !hasPlayed && (
                                  <span style={{ fontSize: '11px', color: 'rgba(255,193,7,0.7)', marginLeft: '4px' }}>⚠️</span>
                                )}
                              </td>
                              <td className="volume-cell">
                                {hasPlayed ? `${referrer.total_volume.toFixed(4)} SOL` : (
                                  <span style={{ color: 'rgba(255,255,255,0.3)' }}>0 SOL</span>
                                )}
                              </td>
                              <td className="earnings-cell">
                                {hasEarnings ? `${referrer.potential_earnings.toFixed(6)} SOL` : (
                                  <span style={{ color: 'rgba(255,255,255,0.3)' }}>0 SOL</span>
                                )}
                              </td>
                              <td className="claimed-cell">
                                {referrer.actual_claimed > 0 ? `${referrer.actual_claimed.toFixed(6)} SOL` : '-'}
                              </td>
                              <td className="claimable-cell">
                                <span className={referrer.claimable_now > 0 ? 'claimable-highlight' : ''}>
                                  {referrer.claimable_now > 0 ? `${referrer.claimable_now.toFixed(6)} SOL` : '-'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ marginTop: '1rem', padding: '12px', background: 'rgba(255,193,7,0.1)', borderRadius: '8px', border: '1px solid rgba(255,193,7,0.2)' }}>
                    <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', margin: '0 0 8px 0' }}>
                      <strong>📖 Understanding the Data:</strong>
                    </p>
                    <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', margin: '4px 0' }}>
                      • <strong>Referrals:</strong> Number of people who signed up with this code
                    </p>
                    <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', margin: '4px 0' }}>
                      • <strong>Total Volume:</strong> SOL wagered by those referrals (only from completed games)
                    </p>
                    <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', margin: '4px 0' }}>
                      • <strong>Potential Earnings:</strong> Volume × 1.25% commission
                    </p>
                    <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', margin: '4px 0' }}>
                      • <strong>⚠️ Warning Icon:</strong> Referrals signed up but haven't played any games yet
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Trends Chart Section */}
          <div className="trends-section">
            <h2 className="trends-title">📈 Daily Game Activity</h2>
            <div className="admin-chart-controls">
              <button 
                className={`admin-chart-btn ${chartRange === '7d' ? 'active' : ''}`}
                onClick={() => handleChartRangeChange('7d')}
              >
                7 Days
              </button>
              <button 
                className={`admin-chart-btn ${chartRange === '30d' ? 'active' : ''}`}
                onClick={() => handleChartRangeChange('30d')}
              >
                30 Days
              </button>
              <button 
                className="admin-chart-btn"
                onClick={() => {
                  if (metrics && metrics.dailyStats) {
                    drawTrendsChart(metrics.dailyStats);
                  }
                }}
                style={{ marginLeft: '10px', backgroundColor: '#333' }}
              >
                🔄 Refresh Chart
              </button>
            </div>
            <div className="admin-chart-container">
              <canvas 
                ref={canvasRef}
                width={800} 
                height={350}
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            </div>
          </div>

          {error && <div className="admin-error">{error}</div>}
        </div>
      )}
      </div>
    </div>
  );
};

export default AdminDashboard; 